import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useUploadFormContext } from "./UploadFormContext";
import { useSidebarContext } from "../SidebarContext";

const ToolbarContext = createContext();

export const useToolbarContext = () => {
  const context = useContext(ToolbarContext);
  if (!context) {
    throw new Error("useSocialContext must be used within a SocialProvider");
  }
  return context;
};

export const UPLOAD_DRAWER_TOGGLER_ID = "upload-drawer";

export const ToolbarProvider = ({ children, canSpawnEmojis }) => {
  const [emojisOpen, setEmojisOpen] = useState(false);

  // Since the sidebar context is not always available, we need add a fallback
  const { sidebarId, setSidebar } = useSidebarContext();

  const fileUploadOpen = useMemo(() => sidebarId === UPLOAD_DRAWER_TOGGLER_ID, [sidebarId]);
  const setFileUploadOpen = useCallback(
    open => {
      setSidebar(open ? UPLOAD_DRAWER_TOGGLER_ID : "");
    },
    [setSidebar]
  );

  useEffect(() => {
    window.addEventListener(`open-${UPLOAD_DRAWER_TOGGLER_ID}`, () => {
      setFileUploadOpen(true);
    });

    window.addEventListener(`close-${UPLOAD_DRAWER_TOGGLER_ID}`, () => {
      setFileUploadOpen(false);
    });

    window.addEventListener(`toggle-${UPLOAD_DRAWER_TOGGLER_ID}`, () => {
      setFileUploadOpen(!fileUploadOpen);
    });

    return () => {
      window.removeEventListener(`open-${UPLOAD_DRAWER_TOGGLER_ID}`, () => {
        setFileUploadOpen(true);
      });

      window.removeEventListener(`close-${UPLOAD_DRAWER_TOGGLER_ID}`, () => {
        setFileUploadOpen(false);
      });

      window.removeEventListener(`toggle-${UPLOAD_DRAWER_TOGGLER_ID}`, () => {
        setFileUploadOpen(!fileUploadOpen);
      });
    };
  }, [fileUploadOpen, setFileUploadOpen]);

  const formContext = useUploadFormContext() || {};
  const {
    onClear: onClearFileModal,
    isDigitalArt,
    setIsDigitalArt,
    isDigitalTwinArt,
    setIsDigitalTwinArt
  } = formContext;

  const onEmojisOpenChange = useCallback(open => setEmojisOpen(open), []);

  const onDigitalArtChange = useCallback(isDigitalArt => setIsDigitalArt(isDigitalArt), []);
  const onDigitalTwinArtChange = useCallback(isDigitalTwinArt => setIsDigitalTwinArt(isDigitalTwinArt), []);

  const onCloseFileUploadOpen = useCallback(() => {
    setIsDigitalArt(false);
    setIsDigitalTwinArt(false);
    setFileUploadOpen(false);
  }, []);

  const onFileUploadOpen = useCallback(open => {
    if (!open) {
      onClearFileModal();
    }
    setFileUploadOpen(open);
  }, []);

  return (
    <ToolbarContext.Provider
      value={{
        emojisOpen,
        fileUploadOpen,
        onEmojisOpenChange,
        canSpawnEmojis,
        onDigitalArtChange,
        onDigitalTwinArtChange,
        isDigitalArt,
        isDigitalTwinArt,
        onCloseFileUploadOpen,
        setFileUploadOpen,
        onFileUploadOpen
      }}
    >
      {children}
    </ToolbarContext.Provider>
  );
};

ToolbarProvider.propTypes = {
  children: PropTypes.node,
  canSpawnEmojis: PropTypes.bool
};
